import { VoyagePlansModelBase, PlanSource } from './voyage-plans-model';
import { PlansService } from '../services/plans-service';
import { ProgressModel } from './progress-model';
import { VoyagePlan } from "./vessel-planning-model";
import { LatestNoPlanReminder } from '../services/types/plans-service.types';

export interface PlanCache
{
    dateRequestedTs: Date;
    plans: Map<string, VoyagePlan> | null;
}

export interface LatestNoPlanReminderCache
{
    dateRequestedTs: Date;
    reminder: LatestNoPlanReminder | null;
}

export class VoyagePlansModelImpl extends VoyagePlansModelBase {

    getPlans(imoNo: number):  Map<string, VoyagePlan>|null
    {
        const cache = this._plansCache.get(imoNo);
        return !cache ? null : cache.plans;
    }

    getLatestNoPlanReminder(imoNo: number): LatestNoPlanReminder|null
    {
        const cache = this._latestNoPlanReminderCache.get(imoNo);
        return !cache ? null : cache.reminder;
    }

    // Basic selection for the time being, depends on the plan data source.
    choosePlan(imo: number): VoyagePlan|null
    {
        let latestPlan: VoyagePlan|null = null;
        let latestPlanTime: Date|null = null;
        const plans: Map<string, VoyagePlan>|null = this!.getPlans(imo);
        if (plans != null) {
            for (const plan of plans.values()) {
                const planTime = new Date(plan.create_dtm || "");
        
                if (latestPlanTime === null || planTime > latestPlanTime) {
                    latestPlanTime = planTime;
                    latestPlan = plan;
                }
            }
        }

        return latestPlan;
    }

    async syncPlans(imoNo: number, date: Date)
    {
        // that data should be stored in the TimeSeries DB.
        const previousSyncedPlans = this._plansCache.get(imoNo);
        // if date requested last time is within 10 minutes of what we already have, then no need to request them again
        if (previousSyncedPlans && ((Date.now() - previousSyncedPlans.dateRequestedTs.getTime()) / 60000) <= 10) {
            this.dispatchEvent(new CustomEvent('plans-updated', {detail: {imoNo}}));
            return;
        }

        let plans:Map<string, VoyagePlan>|null = null;

        const progressAction = ProgressModel!.actionStarted('vessel-' + imoNo);
        try {
            plans = await PlansService.fetchPlans(imoNo, date);
        } finally {
            ProgressModel!.actionCompleted(progressAction);
        }

        this._plansCache.set(imoNo, {dateRequestedTs: new Date(), plans: plans});
        this.dispatchEvent(new CustomEvent('plans-updated', {detail: {imoNo}}));
    }

    async syncLatestNoPlanReminder(imoNo: number) {
        // Check cache
        // that data should be stored in the TimeSeries DB.
        const cachedNoPlanReminder = this._latestNoPlanReminderCache.get(imoNo);
      
        // if date requested last time is within 5 minutes of what we already have, then no need to request them again
        if (cachedNoPlanReminder && ((Date.now() - cachedNoPlanReminder.dateRequestedTs.getTime()) / 60000) <= 5) {
            this.dispatchEvent(new CustomEvent('latest-reminders-updated', {detail: {imoNo}}));
            return;
        }


        let latestNoPlanReminder: LatestNoPlanReminder|null = null;
        const progressAction = ProgressModel!.actionStarted('vessel-' + imoNo);
        try {
            latestNoPlanReminder = await PlansService.getlatestNoPlanReminder(imoNo);
        } finally {
            ProgressModel!.actionCompleted(progressAction);
        }

        this._latestNoPlanReminderCache.set(imoNo, {dateRequestedTs: new Date(), reminder: latestNoPlanReminder});
        this.dispatchEvent(new CustomEvent('latest-reminders-updated', {detail: {imoNo}}));
    }

    private _plansCache: Map<number, PlanCache> = new Map();
    private _latestNoPlanReminderCache: Map<number, LatestNoPlanReminderCache> = new Map();
}
